import { useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { MaskedLinesOnScroll, Reveal } from "@/components/motion/Reveal";

export default function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        email,
        name: name || null,
        source: "project-requirements-form",
      });
      setDone(true);
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      data-testid="lead-magnet-section"
      className="relative bg-navy-deep border-b border-[#C6A87C]/10 overflow-hidden"
    >
      <div className="absolute inset-0 noise-overlay opacity-[0.08] pointer-events-none" />
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <div className="label-overline flex items-center gap-2">
              <DownloadSimple size={14} weight="light" /> Project Requirements Form
            </div>
          </Reveal>
          <MaskedLinesOnScroll
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F5F4F0]"
            lines={[
              <>The single document that</>,
              <>
                captures <em className="italic text-[#C6A87C]">all your requirements</em>
              </>,
              <>for a Patent Analytics project.</>,
            ]}
          />
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#8A99AA] font-light leading-relaxed max-w-xl text-lg">
              Tell us where to send the form. Venkataramanan will email it to you
              personally — and follow up with a free 15-minute call if you&rsquo;d like
              to discuss your specific matter.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            {done ? (
              <div
                data-testid="lead-magnet-success"
                className="bg-navy border border-[#C6A87C]/50 p-8 lg:p-10 flex items-start gap-4"
              >
                <CheckCircle size={28} weight="light" className="text-[#C6A87C] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-serif text-2xl text-[#F5F4F0]">Check your inbox.</h3>
                  <p className="mt-2 text-[#8A99AA] font-light leading-relaxed">
                    Venkataramanan will personally email you the Project Requirements
                    Form within one business day — along with a short note inviting
                    you to a free 15-minute discovery call if you&rsquo;d like to discuss
                    your specific matter.
                  </p>
                </div>
              </div>
            ) : (
              <form
                data-testid="lead-magnet-form"
                onSubmit={submit}
                className="bg-navy border border-[#C6A87C]/20 p-6 lg:p-8 space-y-4"
              >
                <input
                  data-testid="lead-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full h-12 px-4 bg-transparent border border-[#C6A87C]/25 text-[#F5F4F0] placeholder:text-[#8A99AA]/50 focus:border-[#C6A87C] outline-none transition-colors duration-300 text-sm"
                />
                <input
                  data-testid="lead-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full h-12 px-4 bg-transparent border border-[#C6A87C]/25 text-[#F5F4F0] placeholder:text-[#8A99AA]/50 focus:border-[#C6A87C] outline-none transition-colors duration-300 text-sm"
                />
                <button
                  data-testid="lead-submit"
                  type="submit"
                  disabled={submitting}
                  className="group relative w-full h-12 bg-[#C6A87C] text-[#061324] text-xs font-semibold tracking-[0.16em] uppercase overflow-hidden disabled:opacity-50"
                >
                  <span className="absolute inset-0 bg-[#E2C69D] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
                  <span className="relative inline-flex items-center gap-2">
                    {submitting ? "Sending..." : "Send me the form"}
                    <ArrowRight size={14} weight="bold" />
                  </span>
                </button>
                <p className="text-xs text-[#8A99AA]/70 leading-relaxed font-light">
                  We send one email with the form. No newsletters, no spam — and
                  you can reply directly to share your project details with Venkataramanan.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
